import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Plus, Pencil, Trash2, Save, Users, Phone, Mail, Percent,
  Euro, Search, ChevronDown, ChevronRight, Loader2, Package, Layers, Puzzle,
  FileSpreadsheet, Calendar, Download, FileCheck2, FileX, ShieldCheck, SendHorizonal
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_ICONS = { pacchetto: Package, combo: Layers, addon: Puzzle };
const TIPO_COLORS = {
  pacchetto: "bg-indigo-50 text-indigo-700 border-indigo-200",
  combo: "bg-amber-50 text-amber-700 border-amber-200",
  addon: "bg-emerald-50 text-emerald-700 border-emerald-200"
};

const formatDateIT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch (e) { console.warn("[VenditoriPanel] format date failed:", e); return "—"; }
};

// ─── REMINDER BUTTON ─────────────────────────────────────────────────────────
function ReminderButton({ vendId, headers, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handle = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/superadmin/venditori/${vendId}/invia-promemoria-contratto`,
        { method: "POST", headers }
      );
      if (res.ok) {
        setSent(true);
        onSent?.();
        toast.success("Promemoria inviato via email!");
        setTimeout(() => setSent(false), 4000);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Errore nell'invio del promemoria");
      }
    } catch { toast.error("Errore di connessione"); }
    setLoading(false);
  };

  return (
    <button
      onClick={handle}
      disabled={loading || sent}
      data-testid={`btn-promemoria-${vendId}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all flex-shrink-0
        ${sent
          ? "bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default"
          : "bg-white border-amber-200 text-amber-700 hover:bg-amber-50 active:scale-95"
        }`}
    >
      {loading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : sent
          ? <ShieldCheck className="h-3.5 w-3.5" />
          : <SendHorizonal className="h-3.5 w-3.5" />
      }
      {loading ? "Invio..." : sent ? "Inviato!" : "Invia promemoria"}
    </button>
  );
}


const VenditoriPanel = () => {
  const confirm = useConfirm();
  const [venditori, setVenditori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [vociDisponibili, setVociDisponibili] = useState([]);
  
  // Report state
  const [reportDialog, setReportDialog] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMese, setReportMese] = useState(new Date().getMonth() + 1);
  const [reportAnno, setReportAnno] = useState(new Date().getFullYear());

  const headers = { Authorization: `Bearer ${localStorage.getItem("sa_token")}` };

  const fetchVenditori = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/venditori`, { headers });
      setVenditori(res.data);
    } catch (e) {
      toast.error("Errore caricamento venditori");
    } finally { setLoading(false); }
  }, []);

  const fetchVoci = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/venditori/provvigioni-voci`, { headers });
      setVociDisponibili(res.data);
    } catch (e) { console.warn("[VenditoriPanel] silent failure:", e); }
  }, []);

  useEffect(() => { fetchVenditori(); fetchVoci(); }, [fetchVenditori, fetchVoci]);

  const buildProvvigioniForm = (existing = []) => {
    return vociDisponibili.map(voce => {
      const saved = existing.find(p => p.voce_id === voce.voce_id);
      return {
        voce_id: voce.voce_id,
        voce_tipo: voce.voce_tipo,
        voce_nome: voce.voce_nome,
        prezzo_riferimento: voce.prezzo_riferimento,
        tipo: saved?.tipo || "percentuale",
        valore: saved?.valore ?? 0
      };
    });
  };

  const emptyForm = {
    nome: "", codice_venditore: "", email: "", telefono: "",
    provvigioni: [], note: "", attivo: true
  };

  const openDialog = (item = null) => {
    setEditItem(item);
    const base = item ? { ...emptyForm, ...item } : { ...emptyForm };
    base.provvigioni = buildProvvigioniForm(item?.provvigioni || []);
    setForm(base);
    setDialog(true);
  };

  const updateProvvigione = (idx, field, value) => {
    setForm(f => {
      const prov = [...f.provvigioni];
      prov[idx] = { ...prov[idx], [field]: value };
      return { ...f, provvigioni: prov };
    });
  };

  const saveVenditore = async () => {
    if (!form.nome?.trim()) { toast.error("Il nome e obbligatorio"); return; }
    const telDigits = (form.telefono || "").replace(/\D/g, "");
    if (telDigits.length < 6) { toast.error("Il numero di telefono e obbligatorio"); return; }
    setSaving(true);
    // Filtra solo provvigioni con valore > 0
    const payload = {
      ...form,
      provvigioni: form.provvigioni
        .filter(p => p.valore > 0)
        .map(({ voce_id, voce_tipo, voce_nome, tipo, valore }) => ({ voce_id, voce_tipo, voce_nome, tipo, valore }))
    };
    try {
      if (editItem) {
        await axios.put(`${API}/superadmin/venditori/${editItem.id}`, payload, { headers });
        toast.success("Venditore aggiornato");
      } else {
        await axios.post(`${API}/superadmin/venditori`, payload, { headers });
        toast.success("Venditore creato");
      }
      setDialog(false);
      fetchVenditori();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const deleteVenditore = async (id) => {
    if (!await confirm("Eliminare questo venditore?")) return;
    try {
      await axios.delete(`${API}/superadmin/venditori/${id}`, { headers });
      toast.success("Eliminato");
      fetchVenditori();
    } catch { toast.error("Errore"); }
  };

  const toggleExpand = async (vendId) => {
    if (expandedId === vendId) { setExpandedId(null); return; }
    setExpandedId(vendId);
    try {
      const res = await axios.get(`${API}/superadmin/venditori/${vendId}/clienti`, { headers });
      setClienti(res.data);
    } catch { setClienti([]); }
  };

  const getProvvigioneSummary = (provvigioni = []) => {
    const active = (provvigioni || []).filter(p => p.valore > 0);
    if (active.length === 0) return "Nessuna provvigione";
    return `${active.length} voci configurate`;
  };

  // Assegna società state
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignVenditoreId, setAssignVenditoreId] = useState(null);
  const [societaLibere, setSocietaLibere] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedSocietaId, setSelectedSocietaId] = useState("");

  const openAssignDialog = async (venditoreId) => {
    setAssignVenditoreId(venditoreId);
    setSelectedSocietaId("");
    setAssignDialog(true);
    try {
      const res = await axios.get(`${API}/superadmin/societa-senza-venditore`, { headers });
      setSocietaLibere(res.data);
    } catch { setSocietaLibere([]); toast.error("Errore caricamento società"); }
  };

  const assegnaSocieta = async () => {
    if (!selectedSocietaId) { toast.error("Seleziona una società"); return; }
    setAssignLoading(true);
    try {
      await axios.post(`${API}/superadmin/venditori/${assignVenditoreId}/assegna-societa`, { societa_id: selectedSocietaId }, { headers });
      toast.success("Società assegnata al venditore!");
      setAssignDialog(false);
      fetchVenditori();
      if (expandedId === assignVenditoreId) {
        const res = await axios.get(`${API}/superadmin/venditori/${assignVenditoreId}/clienti`, { headers });
        setClienti(res.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'assegnazione"); }
    finally { setAssignLoading(false); }
  };

  // Report functions
  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/venditori/report?mese=${reportMese}&anno=${reportAnno}`, { headers });
      setReportData(res.data);
    } catch (e) {
      toast.error("Errore caricamento report");
    } finally { setReportLoading(false); }
  };

  const openReportDialog = () => {
    setReportDialog(true);
    fetchReport();
  };

  const exportReport = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/venditori/report/export?mese=${reportMese}&anno=${reportAnno}`, {
        headers,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_provvigioni_${reportMese}_${reportAnno}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report esportato!");
    } catch { toast.error("Errore export"); }
  };

  const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

  const filtered = venditori.filter(v =>
    !search || v.nome?.toLowerCase().includes(search.toLowerCase()) || v.codice_venditore?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-5" data-testid="venditori-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900">Venditori / Agenti</h2>
          <p className="text-sm text-slate-500 mt-0.5">{venditori.length} venditori registrati</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openReportDialog} className="rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 font-semibold" data-testid="report-provvigioni-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Report Provvigioni
          </Button>
          <Button onClick={() => openDialog()} className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold" data-testid="add-venditore-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuovo Venditore
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          className="pl-10 h-10 rounded-xl bg-white border-slate-200 text-slate-900" placeholder="Cerca per nome o codice..." data-testid="search-venditori" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center border border-slate-200">
          <Users className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">{search ? "Nessun risultato" : "Nessun venditore registrato"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(v => (
            <div key={v.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`venditore-${v.id}`}>
              <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(v.id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${v.attivo ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
                    {v.nome?.charAt(0)?.toUpperCase() || "V"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-900 truncate">{v.nome}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-500 font-mono">{v.codice_venditore}</span>
                      {!v.attivo && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-50 text-red-500">INATTIVO</span>}
                      {v.contratto_accettato
                        ? <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-50 text-emerald-600 border border-emerald-100" data-testid={`badge-contratto-firmato-${v.id}`}><FileCheck2 className="h-3 w-3" />Contratto firmato</span>
                        : <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-50 text-amber-600 border border-amber-100" data-testid={`badge-contratto-pendente-${v.id}`}><FileX className="h-3 w-3" />Da firmare</span>
                      }
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      {v.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{v.email}</span>}
                      {v.telefono && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{v.telefono}</span>}
                      <span className="flex items-center gap-1">
                        <Percent className="h-3 w-3" />
                        {getProvvigioneSummary(v.provvigioni)}
                      </span>
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{v.clienti_count || 0} clienti</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="ghost" onClick={e => { e.stopPropagation(); openDialog(v); }} className="h-8 w-8 text-slate-500 hover:text-slate-900">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={e => { e.stopPropagation(); deleteVenditore(v.id); }} className="h-8 w-8 text-slate-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  {expandedId === v.id ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded: provvigioni + clienti */}
              {expandedId === v.id && (
                <div className="px-5 pb-5 border-t border-slate-100">
                  {/* Riepilogo provvigioni */}
                  {(v.provvigioni || []).filter(p => p.valore > 0).length > 0 && (
                    <div className="mt-3 mb-4">
                      <p className="text-xs font-bold text-slate-400 uppercase mb-2">Provvigioni</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {(v.provvigioni || []).filter(p => p.valore > 0).map((p, i) => {
                          const Icon = TIPO_ICONS[p.voce_tipo] || Package;
                          return (
                            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${TIPO_COLORS[p.voce_tipo] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate flex-1">{p.voce_nome}</span>
                              <span className="font-bold whitespace-nowrap">{p.valore}{p.tipo === "percentuale" ? "%" : "€"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Contratto Commerciale */}
                  <div className="mt-3 mb-4">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-2">Contratto Commerciale</p>
                    {v.contratto_accettato ? (
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 flex items-center gap-3" data-testid={`contratto-firmato-${v.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <ShieldCheck className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-emerald-700">Firmato digitalmente</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {formatDateIT(v.contratto_accettato_at)}
                            {v.contratto_versione_accettata && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold text-[10px]">Ver. {v.contratto_versione_accettata}</span>}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`contratto-pendente-${v.id}`}>
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <FileX className="h-4 w-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-amber-700">Non ancora firmato</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {v.promemoria_contratto_at
                                ? `Ultimo promemoria: ${formatDateIT(v.promemoria_contratto_at)}`
                                : "Il venditore accetterà il contratto al primo accesso"}
                            </p>
                          </div>
                        </div>
                        {v.email && (
                          <ReminderButton vendId={v.id} headers={headers} onSent={() => {
                            setVenditori(prev => prev.map(x => x.id === v.id
                              ? { ...x, promemoria_contratto_at: new Date().toISOString() }
                              : x
                            ));
                          }} />
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-xs font-bold text-slate-400 uppercase mt-3 mb-2">Clienti associati</p>
                  <div className="flex items-center justify-between mb-2">
                    <span></span>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openAssignDialog(v.id); }}
                      className="rounded-xl text-xs font-semibold border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      data-testid={`assign-societa-${v.id}`}>
                      <Plus className="h-3 w-3 mr-1" /> Assegna Società
                    </Button>
                  </div>
                  {clienti.length === 0 ? (
                    <p className="text-sm text-slate-400">Nessun cliente associato</p>
                  ) : (
                    <div className="space-y-2">
                      {clienti.map(c => (
                        <div key={c.societa_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                          <span className="text-sm font-medium text-slate-700">{c.nome || c.societa_id}</span>
                          <span className={`text-xs font-bold ${c.attivo ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {c.attivo ? "Attivo" : "Inattivo"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagamenti provvigioni */}
                  <PagamentiSuperAdmin venditoreId={v.id} headers={headers} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog Crea/Modifica Venditore */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-black">{editItem ? "Modifica" : "Nuovo"} Venditore</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Info base */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">Nome *</Label>
                <Input value={form.nome || ""} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  className="mt-1 rounded-xl" placeholder="Mario Rossi" data-testid="venditore-nome" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Codice (auto se vuoto)</Label>
                <Input value={form.codice_venditore || ""} onChange={e => setForm(f => ({ ...f, codice_venditore: e.target.value.toUpperCase() }))}
                  className="mt-1 rounded-xl font-mono" placeholder="V00123" data-testid="venditore-codice" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">Email</Label>
                <Input value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="mt-1 rounded-xl" placeholder="mario@email.com" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Telefono <span className="text-red-500">*</span></Label>
                <Input value={form.telefono || ""} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                  className="mt-1 rounded-xl" placeholder="+39 333 1234567" required data-testid="venditore-telefono-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Password accesso</Label>
                <Input type="text" value={form.password || ""} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="mt-1 rounded-xl" placeholder={form.id ? "Lascia vuoto per non cambiare" : "Password iniziale"} />
                <p className="text-[10px] text-muted-foreground mt-0.5">{form.id ? "Compila solo per resettare la password" : "Il venditore usera questa password per il primo accesso"}</p>
              </div>
            </div>

            {/* Tabella Provvigioni Granulare Redesign */}
            <div>
              <div className="mb-4">
                <Label className="text-sm font-bold text-gray-900 block">Configurazione Provvigioni</Label>
                <p className="text-xs text-gray-500 mt-1">Imposta il valore delle provvigioni. Le voci attive (valore &gt; 0) saranno evidenziate. Scegli tra percentuale o importo fisso.</p>
              </div>
              
              <div className="space-y-4">
                {["pacchetto", "combo", "addon"].map(tipo => {
                  const rows = (form.provvigioni || []).filter(p => p.voce_tipo === tipo);
                  if (rows.length === 0) return null;
                  
                  const Icon = TIPO_ICONS[tipo] || Package;
                  const labels = { pacchetto: "Pacchetti Base", combo: "Offerte Combo", addon: "Add-on" };
                  
                  // Colori delle card header per far risaltare ogni sezione
                  const headerStyles = {
                    pacchetto: "bg-indigo-50/80 text-indigo-900 border-indigo-100",
                    combo: "bg-amber-50/80 text-amber-900 border-amber-100",
                    addon: "bg-emerald-50/80 text-emerald-900 border-emerald-100"
                  };
                  
                  const iconStyles = {
                    pacchetto: "text-indigo-600 bg-indigo-100",
                    combo: "text-amber-600 bg-amber-100",
                    addon: "text-emerald-600 bg-emerald-100"
                  };

                  return (
                    <div key={tipo} className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                      <div className={`px-4 py-3 flex items-center gap-3 border-b ${headerStyles[tipo]}`}>
                        <div className={`p-1.5 rounded-lg ${iconStyles[tipo]}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <h3 className="font-bold text-sm tracking-tight">{labels[tipo]}</h3>
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider opacity-70">
                          {rows.filter(r => r.valore > 0).length} attive su {rows.length}
                        </span>
                      </div>
                      
                      <div className="divide-y divide-gray-100">
                        {rows.map((prov) => {
                          const globalIdx = form.provvigioni.findIndex(p => p.voce_id === prov.voce_id);
                          const isActive = prov.valore > 0;
                          
                          return (
                            <div key={prov.voce_id} className={`p-4 transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isActive ? 'bg-indigo-50/30' : 'hover:bg-gray-50/50'}`} data-testid={`prov-row-${prov.voce_id}`}>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-sm font-bold truncate ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {prov.voce_nome}
                                  </span>
                                  {isActive && (
                                    <span className="px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase tracking-wider shrink-0">
                                      Attiva
                                    </span>
                                  )}
                                </div>
                                
                                {prov.prezzo_riferimento > 0 ? (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                                    Listino: <span className="text-gray-700 font-semibold">{prov.prezzo_riferimento}€ / mese</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-200"></span>
                                    Nessun prezzo base
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-3 shrink-0">
                                {/* Segmented Toggle per Tipo */}
                                <div className="flex items-center p-0.5 rounded-lg bg-gray-100 border border-gray-200 shadow-inner h-9">
                                  <button
                                    type="button"
                                    onClick={() => updateProvvigione(globalIdx, "tipo", "percentuale")}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all h-full flex items-center justify-center ${prov.tipo === 'percentuale' ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                                    data-testid={`btn-tipo-perc-${prov.voce_id}`}
                                  >
                                    %
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateProvvigione(globalIdx, "tipo", "fissa")}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all h-full flex items-center justify-center ${prov.tipo === 'fissa' ? 'bg-white text-gray-900 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                                    data-testid={`btn-tipo-fisso-${prov.voce_id}`}
                                  >
                                    €
                                  </button>
                                </div>
                                
                                {/* Input Valore */}
                                <div className="relative w-24">
                                  <Input
                                    type="number" step="0.5" min={0}
                                    value={prov.valore || ""}
                                    onChange={e => updateProvvigione(globalIdx, "valore", parseFloat(e.target.value) || 0)}
                                    className={`h-9 pr-7 text-right text-sm font-bold shadow-sm transition-all ${isActive ? 'border-indigo-300 bg-white ring-1 ring-indigo-100' : 'border-gray-200 bg-gray-50'}`}
                                    placeholder="0"
                                    data-testid={`prov-valore-${prov.voce_id}`}
                                  />
                                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-bold text-gray-400 pointer-events-none">
                                    {prov.tipo === "percentuale" ? "%" : "€"}
                                  </span>
                                </div>
                              </div>
                              
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-600">Note interne</Label>
              <Input value={form.note || ""} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                className="mt-1 rounded-xl" placeholder="Es. Zona Roma Nord..." />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
              <span className="text-sm text-gray-700">Attivo</span>
              <Switch checked={form.attivo !== false} onCheckedChange={v => setForm(f => ({ ...f, attivo: v }))} data-testid="venditore-attivo-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveVenditore} disabled={saving}
              className="w-full rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold" data-testid="save-venditore-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ REPORT PROVVIGIONI DIALOG ═══ */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl p-0" data-testid="report-dialog">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" /> Report Provvigioni Mensili
                </h2>
                <p className="text-sm text-white/70 mt-0.5">Calcolo automatico con pro-rata attivazione</p>
              </div>
              <Button variant="outline" onClick={exportReport} className="border-white/30 text-white hover:bg-white/10 hover:text-white text-xs" data-testid="export-report-btn">
                <Download className="h-3.5 w-3.5 mr-1" /> Esporta Excel
              </Button>
            </div>
            
            {/* Selettore Mese/Anno */}
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
                <Calendar className="h-4 w-4 text-white/70" />
                <select 
                  value={reportMese} 
                  onChange={e => { setReportMese(parseInt(e.target.value)); }}
                  className="bg-transparent text-white text-sm font-medium outline-none cursor-pointer"
                  data-testid="select-mese"
                >
                  {MESI.map((m, i) => <option key={i} value={i + 1} className="text-gray-900">{m}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
                <select 
                  value={reportAnno} 
                  onChange={e => { setReportAnno(parseInt(e.target.value)); }}
                  className="bg-transparent text-white text-sm font-medium outline-none cursor-pointer"
                  data-testid="select-anno"
                >
                  {[2024, 2025, 2026].map(y => <option key={y} value={y} className="text-gray-900">{y}</option>)}
                </select>
              </div>
              <Button size="sm" onClick={fetchReport} disabled={reportLoading} className="bg-white/20 hover:bg-white/30 text-white hover:text-white rounded-xl" data-testid="refresh-report-btn">
                {reportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna"}
              </Button>
            </div>
          </div>

          <div className="p-5 overflow-y-auto max-h-[55vh]">
            {reportLoading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            )}

            {!reportLoading && reportData && (
              <div className="space-y-5">
                {/* Totale Generale */}
                <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-4 flex items-center justify-between" data-testid="totale-provvigioni">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Euro className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Totale Provvigioni da Pagare</p>
                      <p className="text-[10px] text-emerald-600">{reportData.periodo?.label}</p>
                    </div>
                  </div>
                  <span className="text-3xl font-black text-emerald-700">{reportData.totale_provvigioni_mensili?.toFixed(2)}€</span>
                </div>

                {/* Lista Venditori */}
                {reportData.venditori?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessun venditore con provvigioni per questo periodo</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reportData.venditori?.map(v => (
                      <div key={v.venditore_id} className="rounded-xl border border-gray-200 overflow-hidden" data-testid={`report-venditore-${v.venditore_id}`}>
                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700">
                              {v.nome?.charAt(0)?.toUpperCase() || "V"}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{v.nome}</p>
                              <p className="text-[10px] text-gray-500">{v.codice} &middot; {v.clienti_count} clienti</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-black text-indigo-700">{v.provvigione_totale?.toFixed(2)}€</p>
                            <p className="text-[10px] text-gray-400">provvigione mensile</p>
                          </div>
                        </div>
                        
                        {/* Dettaglio clienti */}
                        {v.clienti?.length > 0 && (
                          <div className="px-4 py-3 space-y-2 bg-white">
                            {v.clienti.map((c, i) => (
                              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{c.nome}</p>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                    <span>{c.pacchetti?.join(", ") || "-"}</span>
                                    {c.pro_rata < 1 && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
                                        Pro-rata {Math.round(c.pro_rata * 100)}%
                                      </span>
                                    )}
                                    {c.data_inizio && <span>dal {c.data_inizio}</span>}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-gray-800">{c.provvigione?.toFixed(2)}€</p>
                                  <p className="text-[10px] text-gray-400">su {c.costo_mensile?.toFixed(0)}€</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Assegna Società */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-black">Assegna Società</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Seleziona una società da associare a questo venditore. Solo le società senza venditore sono visibili.</p>
            <select
              value={selectedSocietaId}
              onChange={e => setSelectedSocietaId(e.target.value)}
              className="w-full h-10 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3"
              data-testid="select-assign-societa"
            >
              <option value="">— Seleziona società —</option>
              {societaLibere.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
            {societaLibere.length === 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">Tutte le società hanno già un venditore assegnato.</p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssignDialog(false)} className="rounded-xl text-xs">Annulla</Button>
            <Button onClick={assegnaSocieta} disabled={!selectedSocietaId || assignLoading}
              className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold"
              data-testid="confirm-assign-societa">
              {assignLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
              Assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenditoriPanel;

// ═══════════ PAGAMENTI SUPERADMIN ═══════════
function PagamentiSuperAdmin({ venditoreId, headers }) {
  const [pagamenti, setPagamenti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [importo, setImporto] = useState("");
  const [periodo, setPeriodo] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [metodo, setMetodo] = useState("bonifico");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/superadmin/venditori/${venditoreId}/pagamenti`, { headers });
        setPagamenti(res.data);
      } catch (e) { console.error("[VenditoriPanel] action failed:", e); }
      setLoaded(true);
    })();
  }, [venditoreId]);

  const handleCreate = async () => {
    if (!importo || parseFloat(importo) <= 0) { toast.error("Inserisci un importo valido"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/superadmin/venditori/${venditoreId}/pagamenti`, {
        venditore_id: venditoreId, importo: parseFloat(importo), periodo, metodo_pagamento: metodo, note
      }, { headers });
      setPagamenti([res.data, ...pagamenti]);
      setShowForm(false);
      setImporto("");
      setNote("");
      toast.success("Pagamento registrato");
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const handleUpload = async (pagamentoId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/superadmin/pagamenti/${pagamentoId}/upload-fattura`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setPagamenti(prev => prev.map(p => p.id === pagamentoId ? { ...p, fattura_url: res.data.fattura_url, stato: "fatturato" } : p));
      toast.success("Fattura caricata");
    } catch { toast.error("Errore upload"); }
  };

  if (!loaded) return null;

  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-400 uppercase">Pagamenti</p>
        <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); setShowForm(!showForm); }}
          className="rounded-xl text-xs font-semibold h-7" data-testid={`nuovo-pagamento-${venditoreId}`}>
          <Plus className="h-3 w-3 mr-1" /> Registra Pagamento
        </Button>
      </div>

      {showForm && (
        <div className="bg-slate-50 rounded-xl p-3 space-y-2 mb-3 border border-slate-200" onClick={e => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.01" value={importo} onChange={e => setImporto(e.target.value)} placeholder="Importo €" className="h-8 text-sm" data-testid="pag-importo" />
            <DateInput monthOnly value={periodo} onChange={(v) => setPeriodo(v)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={metodo} onChange={e => setMetodo(e.target.value)} className="h-8 rounded-md border border-slate-200 px-2 text-sm bg-white">
              <option value="bonifico">Bonifico</option>
              <option value="contanti">Contanti</option>
              <option value="assegno">Assegno</option>
              <option value="altro">Altro</option>
            </select>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (opz.)" className="h-8 text-sm" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={saving} className="flex-1 h-7 text-xs" data-testid="conferma-pagamento">
              {saving ? "..." : "Conferma pagamento"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="h-7 text-xs">Annulla</Button>
          </div>
        </div>
      )}

      {pagamenti.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">Nessun pagamento registrato</p>
      ) : (
        <div className="space-y-2">
          {pagamenti.map(p => (
            <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50" onClick={e => e.stopPropagation()}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">&euro; {p.importo.toFixed(2)}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.stato === "fatturato" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {p.stato === "fatturato" ? "Fatturato" : "Da fatturare"}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400">{p.periodo} &middot; {p.metodo_pagamento}{p.numero_fattura ? ` &middot; Fatt. ${p.numero_fattura}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {p.fattura_url ? (
                  <a href={p.fattura_url.startsWith("http") ? p.fattura_url : `${API.replace("/api","")}${p.fattura_url}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] text-indigo-600"><Download className="h-3 w-3 mr-1" /> PDF</Button>
                  </a>
                ) : (
                  <label>
                    <input type="file" accept=".pdf,.png,.jpg" className="hidden" onChange={e => e.target.files[0] && handleUpload(p.id, e.target.files[0])} />
                    <Button size="sm" variant="ghost" className="h-7 text-[10px] pointer-events-none" tabIndex={-1}>
                      <Download className="h-3 w-3 mr-1 rotate-180" /> Carica
                    </Button>
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
